import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Send, Mail, Users, Search, Loader2, CheckCircle2, FlaskConical, Link2, ArrowLeft } from "lucide-react";
import { Input, Area, Select, ImageInput } from "./fields";
import { HelpTip } from "./HelpTip";
import { LINK_TYPES, getLinkableItems, resolveLinked } from "@/lib/linked";
import {
  listSubscribers, deleteSubscriber, listNewsletters, createNewsletter,
  updateNewsletter, deleteNewsletter, testNewsletter, sendNewsletter,
} from "@/lib/api";

const STATUS_BADGE = {
  draft: { label: "Brouillon", cls: "text-white/60 border-white/20" },
  test: { label: "Test envoyé", cls: "text-amber-400 border-amber-400/40" },
  sent: { label: "Envoyée", cls: "text-electric border-electric/40" },
};

const ButtonsEditor = ({ items = [], onChange }) => {
  const list = items || [];
  const patch = (i, p) => { const c = [...list]; c[i] = { ...c[i], ...p }; onChange(c); };
  return (
    <div className="space-y-2" data-testid="nl-buttons-editor">
      <div className="flex items-center justify-between">
        <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Boutons / Liens</span>
        <button type="button" onClick={() => onChange([...list, { label: "", url: "" }])} className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-electric"><Plus size={13} /> Ajouter</button>
      </div>
      {list.map((b, i) => (
        <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
          <Input label="Texte du bouton" value={b.label} onChange={(v) => patch(i, { label: v })} placeholder="🎬 Regarder la vidéo" />
          <Input label="Lien (interne ou https://)" value={b.url} onChange={(v) => patch(i, { url: v })} placeholder="/formations ou https://…" />
          <button type="button" onClick={() => onChange(list.filter((_, x) => x !== i))} className="text-red-400/70 hover:text-red-400 pb-2.5"><Trash2 size={15} /></button>
        </div>
      ))}
    </div>
  );
};

const Composer = ({ content, initial, onBack, onSaved }) => {
  const [nl, setNl] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [busy, setBusy] = useState("");
  const set = (k, v) => setNl((n) => ({ ...n, [k]: v }));
  const sent = nl.status === "sent";
  const linkItems = getLinkableItems(content, nl.linkedType);

  const applyLink = (type, id) => {
    set("linkedType", type); set("linkedId", id);
    const r = resolveLinked(content, type, id);
    if (r) {
      setNl((n) => ({
        ...n, linkedType: type, linkedId: id,
        title: n.title || r.title,
        text: n.text || [r.dateText, r.timeText, r.locationName].filter(Boolean).join(" · "),
        image: n.image || r.image || "",
        buttons: (n.buttons && n.buttons.length) ? n.buttons : [{ label: r.ctaLabel, url: r.href }],
      }));
      toast.success(`Infos récupérées depuis « ${r.title} »`);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { subject: nl.subject || "", title: nl.title || "", text: nl.text || "", image: nl.image || "", buttons: nl.buttons || [], linkedType: nl.linkedType || "", linkedId: nl.linkedId || "" };
      const res = nl.id ? await updateNewsletter(nl.id, payload) : await createNewsletter(payload);
      setNl(res); onSaved(res);
      toast.success("Newsletter enregistrée");
    } catch { toast.error("Échec de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const doTest = async () => {
    if (!nl.id) { toast.error("Enregistrez d'abord la newsletter."); return; }
    if (!testEmail.trim()) { toast.error("Saisissez une adresse e-mail de test."); return; }
    setBusy("test");
    try { await testNewsletter(nl.id, testEmail.trim()); toast.success(`E-mail test envoyé à ${testEmail}`); onSaved(); }
    catch { toast.error("Échec de l'envoi du test"); }
    finally { setBusy(""); }
  };

  const doSend = async () => {
    if (!nl.id) { toast.error("Enregistrez d'abord la newsletter."); return; }
    if (!window.confirm("Cette newsletter va être envoyée à tous les abonnés actifs. Confirmer l'envoi ?")) return;
    setBusy("send");
    try { const r = await sendNewsletter(nl.id); toast.success(`Newsletter envoyée à ${r.sent} abonné(s).`); onSaved(); onBack(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'envoi"); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-5" data-testid="nl-composer">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-xs text-secondary2 hover:text-electric"><ArrowLeft size={14} /> Retour à la liste</button>

      <div className="rounded border border-white/10 bg-[#050505] p-4 space-y-3">
        <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2 inline-flex items-center gap-2"><Link2 size={12} /> Lier à un contenu (pré-remplissage, zéro double saisie) <HelpTip text="Sélectionnez une Formation, un Événement, le Festival, un Projet ou une Vidéo : le titre, le texte, le visuel et un bouton sont pré-remplis automatiquement. Vous pouvez ensuite tout ajuster." /></span>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Select label="Type" value={nl.linkedType || ""} onChange={(v) => applyLink(v, "")} options={LINK_TYPES} />
          {linkItems.length > 0 && <Select label="Contenu" value={nl.linkedId || ""} onChange={(v) => applyLink(nl.linkedType, v)} options={[{ value: "", label: "— Sélectionner —" }, ...linkItems.map((x) => ({ value: x.id, label: x.label }))]} />}
        </div>
      </div>

      <Input label="Objet de l'e-mail" value={nl.subject} onChange={(v) => set("subject", v)} testid="nl-subject" placeholder="Ex : Nouvelle formation vidéo — inscriptions ouvertes" />
      <Input label="Titre (dans l'e-mail)" value={nl.title} onChange={(v) => set("title", v)} testid="nl-title" />
      <ImageInput label="Visuel (importez votre image Canva, etc.)" value={nl.image} onChange={(v) => set("image", v)} />
      <Area label="Texte" value={nl.text} onChange={(v) => set("text", v)} rows={5} testid="nl-text" />
      <ButtonsEditor items={nl.buttons} onChange={(v) => set("buttons", v)} />
      <p className="text-[11px] text-secondary2">Les réseaux sociaux et le pied de page (site, désinscription) sont ajoutés automatiquement depuis vos paramètres généraux.</p>

      <div className="flex flex-wrap items-center gap-3 border-t border-white/10 pt-4">
        <button onClick={save} disabled={saving || sent} data-testid="nl-save-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white text-xs uppercase tracking-[0.1em] font-semibold hover:border-electric hover:text-electric disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Enregistrer
        </button>
        <div className="flex items-center gap-2">
          <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="email@test.fr" data-testid="nl-test-email"
            className="bg-[#0e0e0e] border border-white/10 rounded-full px-4 py-2 text-white text-sm focus:border-electric outline-none w-48" />
          <button onClick={doTest} disabled={busy === "test" || sent} data-testid="nl-test-btn" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-amber-400/40 text-amber-400 text-xs uppercase tracking-[0.1em] font-semibold hover:bg-amber-400/10 disabled:opacity-50">
            {busy === "test" ? <Loader2 size={14} className="animate-spin" /> : <FlaskConical size={14} />} Envoyer un test
          </button>
        </div>
        <button onClick={doSend} disabled={busy === "send" || sent} data-testid="nl-send-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-electric text-black text-xs uppercase tracking-[0.1em] font-semibold hover:bg-white disabled:opacity-50">
          {busy === "send" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {sent ? "Déjà envoyée" : "Envoyer la newsletter"}
        </button>
      </div>
    </div>
  );
};

export const AdminNewsletter = ({ content = {} }) => {
  const [view, setView] = useState("list"); // list | compose | subscribers
  const [newsletters, setNewsletters] = useState([]);
  const [editing, setEditing] = useState(null);
  const [subs, setSubs] = useState({ subscribers: [], active: 0, total: 0 });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const loadNl = useCallback(async () => { try { const r = await listNewsletters(); setNewsletters(r.newsletters || []); } catch {} }, []);
  const loadSubs = useCallback(async () => { try { const r = await listSubscribers(search, statusFilter); setSubs(r); } catch {} }, [search, statusFilter]);

  useEffect(() => { loadNl(); }, [loadNl]);
  useEffect(() => { if (view === "subscribers") loadSubs(); }, [view, loadSubs]);

  const newDraft = { subject: "", title: "", text: "", image: "", buttons: [], linkedType: "", linkedId: "", status: "draft" };

  const removeNl = async (id) => { if (!window.confirm("Supprimer cette newsletter ?")) return; await deleteNewsletter(id); loadNl(); };
  const removeSub = async (id) => { await deleteSubscriber(id); loadSubs(); };

  return (
    <div className="space-y-6" data-testid="admin-newsletter">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-electric mb-1">Newsletter</div>
          <h2 className="font-serif text-3xl text-white">Newsletters &amp; Abonnés</h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setView("list")} data-testid="nl-tab-list" className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-[0.1em] border ${view !== "subscribers" ? "border-electric text-electric" : "border-white/15 text-white/60"}`}><Mail size={14} /> Newsletters</button>
          <button onClick={() => setView("subscribers")} data-testid="nl-tab-subscribers" className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-[0.1em] border ${view === "subscribers" ? "border-electric text-electric" : "border-white/15 text-white/60"}`}><Users size={14} /> Abonnés ({subs.active})</button>
        </div>
      </div>

      {view === "compose" && editing && (
        <Composer content={content} initial={editing} onBack={() => { setView("list"); loadNl(); }} onSaved={(res) => { if (res) setEditing((e) => ({ ...e, ...res })); loadNl(); }} />
      )}

      {view === "list" && (
        <div className="space-y-4">
          <button onClick={() => { setEditing(newDraft); setView("compose"); }} data-testid="nl-new-btn" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-electric text-black text-xs uppercase tracking-[0.1em] font-semibold hover:bg-white"><Plus size={15} /> Nouvelle newsletter</button>
          {newsletters.length === 0 && <p className="text-secondary2 text-sm">Aucune newsletter pour le moment.</p>}
          <div className="space-y-2">
            {newsletters.map((n) => {
              const b = STATUS_BADGE[n.status] || STATUS_BADGE.draft;
              return (
                <div key={n.id} data-testid={`nl-row-${n.id}`} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-surface/40 px-4 py-3">
                  <button onClick={() => { setEditing(n); setView("compose"); }} className="text-left min-w-0 flex-1">
                    <div className="text-white truncate">{n.subject || "(Sans objet)"}</div>
                    <div className="text-[11px] text-secondary2">{n.title || "—"}{n.status === "sent" ? ` · ${n.recipients_count} destinataires` : ""}</div>
                  </button>
                  <span className={`text-[10px] uppercase tracking-[0.15em] border rounded-full px-3 py-1 ${b.cls}`}>{b.label}</span>
                  <button onClick={() => removeNl(n.id)} className="text-red-400/70 hover:text-red-400"><Trash2 size={15} /></button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "subscribers" && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un e-mail…" data-testid="nl-search"
                className="bg-[#0e0e0e] border border-white/10 rounded-full pl-9 pr-4 py-2 text-white text-sm focus:border-electric outline-none w-64" />
            </div>
            <Select label="" value={statusFilter} onChange={setStatusFilter} options={[{ value: "", label: "Tous" }, { value: "subscribed", label: "Abonnés" }, { value: "unsubscribed", label: "Désabonnés" }]} />
            <span className="text-xs text-secondary2">{subs.active} actifs · {subs.total} au total</span>
          </div>
          <div className="rounded-lg border border-white/10 overflow-hidden">
            {(subs.subscribers || []).length === 0 && <div className="px-4 py-6 text-secondary2 text-sm">Aucun abonné.</div>}
            {(subs.subscribers || []).map((s) => (
              <div key={s.id} data-testid={`nl-sub-${s.id}`} className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-white/5 last:border-0">
                <span className="text-white text-sm truncate">{s.email}</span>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] uppercase tracking-[0.15em] ${s.status === "subscribed" ? "text-electric" : "text-white/40"}`}>{s.status === "subscribed" ? "Abonné" : "Désabonné"}</span>
                  <button onClick={() => removeSub(s.id)} className="text-red-400/60 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
